/**
 * InfinitSales - UpgradeModal
 *
 * Modal de upsell para Conta Empresa. Aceita dois modos:
 *   - mode='plan_required'  → user não tem plano Business e tentou criar empresa
 *   - mode='seat_limit'     → admin atingiu COMPANY_MAX_TOTAL_USERS membros
 *
 * Quando aberto, busca `subscriptionService.getPlans()` para encontrar o
 * plano Business e renderiza um card visual com preço, lista de benefícios
 * e CTA primário que redireciona para o checkout Stripe (via
 * `subscriptionService.getCheckoutUrl(planId)`).
 *
 * Nunca mostra a `message` crua do backend — o `code` decide o título e a
 * variante; mensagem do backend é apenas log interno.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X,
  Sparkles,
  ExternalLink,
  Check,
  Loader2,
  Users,
  Building2,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import subscriptionService from '../services/global/subscriptionService';
import useFeatureFlags from '../hooks/useFeatureFlags';
import { includedSeatsOf } from '../utils/seats';

// Plan slug substring that identifies the Business plan in InfinitAgents'
// response. The plans endpoint returns objects with `slug` like
// `sales_business`, `sales_pro`, `sales_free` — we match by substring so
// the modal is robust to renaming.
const BUSINESS_SLUG_HINT = 'business';

const formatBRL = (value) =>
  (Number(value) || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

const UpgradeModal = ({ open, onClose, code, message, extraSeatPriceBrl }) => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { flags } = useFeatureFlags();
  // Cadeiras (2026-10-01): qualquer plano vira empresa; o que falta é um
  // plano pago (o Free inclui só o admin) ou mais usuários.
  const seatsBilling = !!flags.company_seats_billing;
  const [plans, setPlans] = useState(null);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // Decide variant from code rather than parsing the backend message.
  const isSeatLimit = code === 'seat_limit_reached';

  useEffect(() => {
    if (!open) return;
    // Fetch plans only for the plan_required variant — seat_limit doesn't
    // need plan data (it just shows the per-seat price and a soft CTA).
    if (isSeatLimit) return;

    let alive = true;
    setLoadingPlans(true);
    subscriptionService
      .getPlans()
      .then((res) => {
        if (!alive) return;
        const list = res?.data || res || [];
        setPlans(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!alive) return;
        // Fallback path: render generic upgrade message + /plans CTA.
        setPlans([]);
      })
      .finally(() => alive && setLoadingPlans(false));
    return () => {
      alive = false;
    };
  }, [open, isSeatLimit]);

  // Find the Business plan. Match by slug substring or by tier hint to be
  // forward-compatible with renamings (e.g. `sales_business_v2`).
  const businessPlan = useMemo(() => {
    if (!Array.isArray(plans)) return null;
    return (
      plans.find(
        (p) =>
          (p.slug || '').toLowerCase().includes(BUSINESS_SLUG_HINT) ||
          (p.tier || '').toLowerCase().includes(BUSINESS_SLUG_HINT) ||
          (p.name || '').toLowerCase().includes(BUSINESS_SLUG_HINT),
      ) || null
    );
  }, [plans]);

  // Price normalization: InfinitAgents returns price as either:
  //  - cents (e.g. 79990 → R$ 799,90), or
  //  - reais (e.g. 799.9). We detect heuristically: integers > 1000 → cents.
  const businessPriceBRL = useMemo(() => {
    if (!businessPlan) return null;
    const raw = businessPlan.price_brl ?? businessPlan.price ?? null;
    if (raw == null) return null;
    if (Number.isInteger(raw) && raw >= 1000) return raw / 100; // cents → reais
    return Number(raw);
  }, [businessPlan]);

  // Synthesize a feature list. Use real plan attributes when available,
  // otherwise fall back to canonical pitch points.
  const businessFeatures = useMemo(() => {
    if (!businessPlan) {
      return [
        'Agente IA compartilhado com seu time',
        'Múltiplos vendedores na mesma assinatura',
        'Painel da empresa com visão consolidada',
        'Billing único — sem dor de cabeça',
      ];
    }
    const out = [];
    // O catalogo (/api/plans) traz os numeros em `limits`; os campos soltos
    // (*_limit) nao vem da API e ficam so como compatibilidade.
    const limits = businessPlan.limits || {};
    if (limits.ai_credits) {
      out.push(`${Number(limits.ai_credits).toLocaleString('pt-BR')} créditos de IA / mês`);
    } else if (businessPlan.tokens_limit) {
      out.push(
        `${subscriptionService.formatTokens(businessPlan.tokens_limit)} créditos IA / mês`,
      );
    }
    const messages = limits.platform_messages ?? businessPlan.messages_limit;
    if (messages) {
      out.push(`${Number(messages).toLocaleString('pt-BR')} mensagens de IA / mês`);
    }
    const agents = limits.agents ?? businessPlan.agents_limit;
    if (agents) {
      out.push(`Até ${agents} agentes IA configuráveis`);
    }
    out.push('Conta Empresa: adicione vendedores que compartilham seu agente');
    out.push('Painel admin: contatos, conversas e relatórios consolidados');
    return out;
  }, [businessPlan]);

  const handleCheckout = async () => {
    if (!businessPlan?.id) {
      // No plan found in API — fall back to /plans page
      onClose?.();
      navigate('/plans');
      return;
    }
    setCheckoutLoading(true);
    try {
      const res = await subscriptionService.getCheckoutUrl(businessPlan.id);
      const url = res?.data?.checkout_url || res?.checkout_url;
      if (url) {
        window.location.href = url;
      } else {
        // Fallback if Stripe URL not generated
        onClose?.();
        navigate('/plans');
      }
    } catch {
      onClose?.();
      navigate('/plans');
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (!open) return null;

  // Discreetly log raw backend message so devs can diagnose without
  // showing it to the end user.
  if (message) console.debug('[UpgradeModal] backend:', code, message);

  const formattedSeatPrice = formatBRL(extraSeatPriceBrl ?? 89.9);

  // Planos pagos com os usuários incluídos (catálogo), para a variante de
  // cadeiras: o cliente escolhe na /plans.
  const paidPlans = (Array.isArray(plans) ? plans : [])
    .filter((p) => Number(p.price_brl) > 0 && (p.product || 'sales') === 'sales')
    .sort((a, b) => Number(a.price_brl) - Number(b.price_brl));
  const planPrice = (p) => {
    const raw = p.price_brl ?? p.price ?? 0;
    return Number.isInteger(raw) && raw >= 1000 ? raw / 100 : Number(raw);
  };

  const styles = {
    backdrop: {
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000,
      padding: '24px',
    },
    modal: {
      backgroundColor: colors.bgPrimary,
      border: `1px solid ${colors.borderColor}`,
      borderRadius: '20px',
      maxWidth: '520px',
      width: '100%',
      overflow: 'hidden',
      boxShadow: '0 16px 60px rgba(0,0,0,0.55)',
    },
    hero: {
      padding: '24px',
      background: `linear-gradient(135deg, ${colors.accentPrimary}30 0%, ${colors.accentPrimary}10 100%)`,
      borderBottom: `1px solid ${colors.borderColor}`,
      position: 'relative',
    },
    closeBtn: {
      position: 'absolute',
      top: 16,
      right: 16,
      background: 'transparent',
      border: 'none',
      color: colors.textSecondary,
      cursor: 'pointer',
      padding: 4,
      borderRadius: 6,
    },
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '4px 10px',
      backgroundColor: colors.accentPrimary,
      color: 'white',
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.4px',
      textTransform: 'uppercase',
    },
    title: {
      fontSize: 22,
      fontWeight: 700,
      color: colors.textPrimary,
      marginTop: 12,
      marginBottom: 4,
    },
    subtitle: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 1.5,
    },
    priceRow: {
      display: 'flex',
      alignItems: 'baseline',
      gap: 6,
      marginTop: 16,
    },
    price: {
      fontSize: 30,
      fontWeight: 700,
      color: colors.textPrimary,
    },
    priceSuffix: {
      fontSize: 14,
      color: colors.textSecondary,
    },
    body: {
      padding: '20px 24px',
    },
    featuresList: {
      listStyle: 'none',
      margin: 0,
      padding: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
    },
    featureItem: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 10,
      fontSize: 14,
      color: colors.textPrimary,
    },
    checkIcon: {
      width: 20,
      height: 20,
      borderRadius: '50%',
      backgroundColor: `${colors.accentPrimary}25`,
      color: colors.accentPrimary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      marginTop: 1,
    },
    footer: {
      display: 'flex',
      gap: 8,
      justifyContent: 'flex-end',
      padding: '16px 24px 20px 24px',
      borderTop: `1px solid ${colors.borderColor}`,
    },
    btnSecondary: {
      padding: '10px 16px',
      border: `1px solid ${colors.borderColor}`,
      borderRadius: 10,
      backgroundColor: 'transparent',
      color: colors.textPrimary,
      fontSize: 14,
      fontWeight: 500,
      cursor: 'pointer',
    },
    btnPrimary: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      padding: '10px 18px',
      border: 'none',
      borderRadius: 10,
      backgroundColor: colors.accentPrimary,
      color: 'white',
      fontSize: 14,
      fontWeight: 700,
      cursor: 'pointer',
    },
    seatBox: {
      marginTop: 12,
      padding: '14px 16px',
      backgroundColor: colors.bgTertiary,
      borderRadius: 12,
      display: 'flex',
      alignItems: 'center',
      gap: 12,
    },
    seatIcon: {
      width: 36,
      height: 36,
      borderRadius: 10,
      backgroundColor: `${colors.accentPrimary}25`,
      color: colors.accentPrimary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
  };

  return (
    <div style={styles.backdrop} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button style={styles.closeBtn} onClick={onClose} aria-label="Fechar">
          <X size={18} />
        </button>

        {isSeatLimit && seatsBilling ? (
          // ============ VARIANT: TETO DE USUÁRIOS (cadeiras ligadas) ============
          <>
            <div style={styles.hero}>
              <span style={styles.badge}>
                <Users size={11} /> Limite atingido
              </span>
              <div style={styles.title}>Sua empresa chegou ao limite de usuários</div>
              <div style={styles.subtitle}>
                Para ampliar a equipe, fale com o suporte. Usuários além dos incluídos no
                plano custam {formattedSeatPrice} por mês.
              </div>
            </div>
            <div style={styles.footer}>
              <button style={styles.btnSecondary} onClick={onClose}>
                Entendi
              </button>
              <button
                style={styles.btnPrimary}
                onClick={() => {
                  onClose?.();
                  navigate('/plans');
                }}
              >
                Ver planos
                <ExternalLink size={14} />
              </button>
            </div>
          </>
        ) : !isSeatLimit && seatsBilling ? (
          // ============ VARIANT: PLANO PAGO PARA TER MEMBROS (cadeiras ligadas) ============
          <>
            <div style={styles.hero}>
              <span style={styles.badge}>
                <Building2 size={11} /> Conta Empresa
              </span>
              <div style={styles.title}>Adicione sua equipe</div>
              <div style={styles.subtitle}>
                O plano gratuito inclui só o administrador. Nos planos pagos a equipe
                compartilha o agente e a assinatura.
              </div>
            </div>
            <div style={styles.body}>
              {loadingPlans ? (
                <div style={{ color: colors.textSecondary, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                  Carregando planos...
                </div>
              ) : (
                <ul style={styles.featuresList}>
                  {paidPlans.map((p) => (
                    <li key={p.id} style={styles.featureItem}>
                      <span style={styles.checkIcon}>
                        <Check size={12} />
                      </span>
                      {p.name}: {includedSeatsOf(p) ?? '-'} usuários incluídos,{' '}
                      {formatBRL(planPrice(p))}/mês
                    </li>
                  ))}
                  <li style={styles.featureItem}>
                    <span style={styles.checkIcon}>
                      <Check size={12} />
                    </span>
                    Usuário adicional: {formattedSeatPrice}/mês
                  </li>
                </ul>
              )}
            </div>
            <div style={styles.footer}>
              <button style={styles.btnSecondary} onClick={onClose}>
                Agora não
              </button>
              <button
                style={styles.btnPrimary}
                onClick={() => {
                  onClose?.();
                  navigate('/plans');
                }}
              >
                Ver planos
                <ExternalLink size={14} />
              </button>
            </div>
          </>
        ) : isSeatLimit ? (
          // ============ VARIANT: SEAT LIMIT REACHED ============
          <>
            <div style={styles.hero}>
              <span style={styles.badge}>
                <Users size={11} /> Limite atingido
              </span>
              <div style={styles.title}>Adicione mais vendedores à sua empresa</div>
              <div style={styles.subtitle}>
                Seu plano inclui um número fixo de usuários. Para escalar a equipe,
                cada vendedor adicional custa o valor abaixo.
              </div>
              <div style={styles.priceRow}>
                <span style={styles.price}>{formattedSeatPrice}</span>
                <span style={styles.priceSuffix}>/ vendedor / mês</span>
              </div>
            </div>
            <div style={styles.body}>
              <div style={styles.seatBox}>
                <div style={styles.seatIcon}>
                  <Sparkles size={18} />
                </div>
                <div style={{ fontSize: 13, color: colors.textSecondary, lineHeight: 1.55 }}>
                  A cobrança automática de seats extras chegará em breve. Avisaremos
                  por email assim que você puder comprar diretamente no painel.
                </div>
              </div>
            </div>
            <div style={styles.footer}>
              <button style={styles.btnSecondary} onClick={onClose}>
                Entendi
              </button>
              <button
                style={styles.btnPrimary}
                onClick={() => {
                  onClose?.();
                  navigate('/plans');
                }}
              >
                Ver planos
                <ExternalLink size={14} />
              </button>
            </div>
          </>
        ) : (
          // ============ VARIANT: PLAN UPGRADE REQUIRED ============
          <>
            <div style={styles.hero}>
              <span style={styles.badge}>
                <Building2 size={11} /> Plano Business
              </span>
              <div style={styles.title}>
                Desbloqueie a Conta Empresa
              </div>
              <div style={styles.subtitle}>
                Compartilhe seu agente IA com vendedores, mantenha uma assinatura única
                e tenha visão consolidada de toda a operação.
              </div>
              {loadingPlans ? (
                <div style={{ marginTop: 16, color: colors.textSecondary, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                  Carregando plano...
                </div>
              ) : businessPriceBRL != null ? (
                <div style={styles.priceRow}>
                  <span style={styles.price}>{formatBRL(businessPriceBRL)}</span>
                  <span style={styles.priceSuffix}>/ mês</span>
                </div>
              ) : null}
            </div>
            <div style={styles.body}>
              <ul style={styles.featuresList}>
                {businessFeatures.map((f, idx) => (
                  <li key={idx} style={styles.featureItem}>
                    <span style={styles.checkIcon}>
                      <Check size={12} />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
            </div>
            <div style={styles.footer}>
              <button style={styles.btnSecondary} onClick={onClose}>
                Agora não
              </button>
              <button
                style={styles.btnPrimary}
                onClick={handleCheckout}
                disabled={checkoutLoading || loadingPlans}
              >
                {checkoutLoading ? (
                  <>
                    <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                    Redirecionando...
                  </>
                ) : (
                  <>
                    Fazer upgrade para Business
                    <ExternalLink size={14} />
                  </>
                )}
              </button>
            </div>
          </>
        )}

        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
};

export default UpgradeModal;
